import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteCanvas from './InfiniteCanvas';
import CanvasMetadataManager, { CanvasMetadata, CanvasElement } from './CanvasMetadataManager';
import CanvasElementEditModal from './CanvasElementEditModal';
// import { storyApi } from '../services/api'; // Unused import

interface FullscreenInfiniteCanvasProps {
  isVisible: boolean;
  onClose: () => void;
  onStoryBranchVisualize?: (storyContext: any) => void;
}

const FullscreenInfiniteCanvas: React.FC<FullscreenInfiniteCanvasProps> = ({
  isVisible,
  onClose,
  onStoryBranchVisualize
}) => {
  const { t } = useTranslation();
  
  // Canvas state
  const [canvasElements, setCanvasElements] = useState<CanvasElement[]>([]);
  const [canvasMetadata, setCanvasMetadata] = useState<CanvasMetadata>({});
  const [showMetadataManager, setShowMetadataManager] = useState(false);
  const [selectedCanvasElements, setSelectedCanvasElements] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [editingElement, setEditingElement] = useState<CanvasElement | null>(null);

  // Edit Modal handlers
  const handleElementEditStart = (element: CanvasElement) => {
    setEditingElement(element);
  };

  const handleElementEditSave = (elementId: string, updatedData: Partial<CanvasElement>) => {
    handleCanvasElementUpdate(elementId, updatedData);
    setEditingElement(null);
  };

  const handleElementEditCancel = () => {
    setEditingElement(null);
  };

  // Canvas handlers
  const handleCanvasElementAdd = (element: Omit<CanvasElement, 'id'>) => {
    const newElement: CanvasElement = {
      ...element,
      id: `canvas_${Date.now()}_${Math.floor(Math.random() * 10000)}`
    };
    setCanvasElements(prev => [...prev, newElement]);
  };

  const handleCanvasElementUpdate = (id: string, updates: Partial<CanvasElement>) => {
    setCanvasElements(prev => prev.map(el => 
      el.id === id ? { ...el, ...updates } : el
    ));
  };

  const handleCanvasElementDelete = (id: string) => {
    setCanvasElements(prev => prev.filter(el => el.id !== id));
  };

  const handleCanvasElementsSelect = (elementIds: string[]) => {
    setSelectedCanvasElements(elementIds);
  };

  const handleCanvasElementRightClick = (element: CanvasElement, x: number, y: number) => {
    console.log('Element right-clicked:', element);
  };

  const handleCanvasRightClick = (x: number, y: number) => {
    console.log('Canvas right-clicked at:', x, y);
  };

  const handleCanvasMetadataUpdate = (metadata: CanvasMetadata) => {
    setCanvasMetadata(metadata);
  };



  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'linear-gradient(135deg, #1a2a6c 0%, #16213e 50%, #3a6186 100%)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div
        style={{
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '16px 24px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h2 style={{ 
            color: '#ffffff', 
            margin: 0, 
            fontSize: '24px',
            background: 'linear-gradient(45deg, #7209b7, #f72585)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🎨 {t('canvas.fullscreen.title')}
          </h2>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowMetadataManager(true)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(45deg, #4361ee, #4cc9f0)',
                border: 'none',
                borderRadius: '6px',
                color: '#ffffff',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease'
              }}
            >
              ⚙️ {t('canvas.buttons.settings')}
            </button>
            
            {(() => {
              const selectedElements = selectedCanvasElements
                .map(id => canvasElements.find(el => el.id === id))
                .filter(el => el);
              const showConnectButton = selectedElements.length >= 2;
              
              return showConnectButton && (
                <button
                  onClick={() => {
                    if (selectedElements.length < 2) return;
                    const firstElement = selectedElements[0];
                    if (!firstElement) return;
                    
                    for (let i = 1; i < selectedElements.length; i++) {
                      const element = selectedElements[i];
                      if (element) {
                        setCanvasElements(prev => prev.map(el => {
                          if (el.id === element.id) {
                            return {
                              ...el,
                              connections: [...(el.connections || []), firstElement.id]
                            };
                          }
                          return el;
                        }));
                      }
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(45deg, #4361ee, #7209b7)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease'
                  }}
                  title={t('canvas.buttons.connectTooltip', { count: selectedElements.length })}
                >
                  🔗 {t('canvas.buttons.connect')}
                </button>
              );
            })()}
            

            
            {(() => {
              const selectedCharacterElements = selectedCanvasElements
                .map(id => canvasElements.find(el => el.id === id))
                .filter(el => el && el.type === 'character');
              const selectedSceneElements = selectedCanvasElements
                .map(id => canvasElements.find(el => el.id === id))
                .filter(el => el && el.type === 'scene');
              
              const showGenerateButton = selectedCharacterElements.length >= 1 && selectedSceneElements.length >= 1;
              
              return showGenerateButton && (
                <button
                  onClick={() => {
                    const characterElements = canvasElements.filter(el => el.type === 'character');
                    const sceneElements = canvasElements.filter(el => el.type === 'scene');
                    const eventElements = canvasElements.filter(el => el.type === 'event');
                    
                    const storyContext = {
                      characters: characterElements.map(el => ({ name: el.title, description: el.content })),
                      scenes: sceneElements.map(el => ({ name: el.title, description: el.content })),
                      events: eventElements.map(el => ({ name: el.title, description: el.content })),
                      worldbuilding: canvasMetadata
                    };
                    
                    // Close canvas first, then pass story context to generate story tree
                    onClose();
                    if (onStoryBranchVisualize) {
                      onStoryBranchVisualize(storyContext);
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(45deg, #7209b7, #f72585)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    transition: 'all 0.2s ease'
                  }}
                  title={t('canvas.buttons.generateTooltip')}
                >
                  ✨ {t('canvas.buttons.generate')}
                </button>
              );
            })()}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
            {t('canvas.stats.elements')}: {canvasElements.length} | {t('canvas.stats.selected')}: {selectedCanvasElements.length}
          </div>
          
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '6px',
              color: '#ffffff',
              cursor: 'pointer',
              padding: '8px 16px',
              fontSize: '14px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            ✕ {t('canvas.fullscreen.close')}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          color: '#ff6b6b',
          padding: '12px 24px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Canvas */}
      <div style={{ flex: 1, position: 'relative' }}>
        <InfiniteCanvas
          elements={canvasElements}
          onElementAdd={handleCanvasElementAdd}
          onElementUpdate={handleCanvasElementUpdate}
          onElementDelete={handleCanvasElementDelete}
          onElementConnect={(fromId, toId) => {
            // Handle element connection
            setCanvasElements(prev => prev.map(el => {
              if (el.id === fromId) {
                return {
                  ...el,
                  connections: [...(el.connections || []), toId]
                };
              }
              return el;
            }));
          }}
          onElementRightClick={handleCanvasElementRightClick}
          onCanvasRightClick={handleCanvasRightClick}
          onElementsSelect={handleCanvasElementsSelect}
          onElementEditStart={handleElementEditStart}
          onStoryBranchVisualize={onStoryBranchVisualize}
          canvasMetadata={canvasMetadata}
        />
      </div>

      {/* Metadata Manager Modal */}
      <CanvasMetadataManager
        metadata={canvasMetadata}
        onMetadataUpdate={handleCanvasMetadataUpdate}
        isVisible={showMetadataManager}
        onClose={() => setShowMetadataManager(false)}
      />

      {/* Element Edit Modal */}
      <CanvasElementEditModal
        element={editingElement}
        isVisible={!!editingElement}
        onSave={handleElementEditSave}
        onCancel={handleElementEditCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default FullscreenInfiniteCanvas;